import { useState } from "react";
import { toast } from "sonner";
import { Handshake, CheckCircle2, Hospital, Stethoscope, Users } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import FormConsent from "@/components/FormConsent";

const PARTNERS = [
  { icon: Hospital, t: "Hospitals", d: "Smooth discharge-to-home coordination with rapid intake." },
  { icon: Stethoscope, t: "Rehab facilities", d: "Continuity of care as patients transition home." },
  { icon: Users, t: "Social workers & case managers", d: "Reliable community partner for your caseload." },
];

const TYPES = ["Hospital", "Rehab facility", "Assisted living", "Social worker / case manager", "Physician practice", "Other"];

export default function ReferralPartners() {
  const [form, setForm] = useState({
    organization_name: "", partner_type: "", contact_name: "", email: "", phone: "", notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [termsOk, setTermsOk] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/referrals", form);
      toast.success("Partnership request submitted!");
      setSubmitted(true);
      track("referral_submitted", { partner_type: form.partner_type });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="section" data-testid="partners-success">
        <div className="container-x max-w-2xl soft-card text-center">
          <CheckCircle2 className="w-14 h-14 text-teal-600 mx-auto" />
          <h1 className="mt-6 text-3xl font-semibold text-slate-900">We'd love to partner with you.</h1>
          <p className="mt-4 text-slate-600">A partnerships lead will reach out within one business day to schedule a brief intro.</p>
        </div>
      </section>
    );
  }

  return (
    <div data-testid="partners-page">
      <section className="section bg-gradient-to-b from-slate-50 to-white">
        <div className="container-x max-w-4xl">
          <div className="eyebrow">Referral Partners</div>
          <h1 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
            Partner with Melo Hopeful Care.
          </h1>
          <p className="mt-5 text-slate-600 leading-relaxed max-w-2xl">
            Give your patients and clients a soft landing at home. We coordinate rapid intake for discharges, rehab transitions and case-managed clients across the Kansas City metro.
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="container-x grid md:grid-cols-3 gap-5">
          {PARTNERS.map(({ icon: Icon, t, d }) => (
            <div key={t} className="soft-card" data-testid={`partner-type-${t.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="w-11 h-11 rounded-xl brand-gradient flex items-center justify-center">
                <Icon className="w-5 h-5 text-white" strokeWidth={1.75} />
              </div>
              <h3 className="mt-5 font-heading font-semibold text-slate-900 text-lg">{t}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x max-w-3xl">
          <form onSubmit={submit} className="soft-card space-y-6" data-testid="partners-form">
            <h2 className="text-2xl font-heading font-semibold text-slate-900">Tell us about your organization</h2>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="label-field">Organization name *</label>
                <input required value={form.organization_name} onChange={upd("organization_name")} className="input-field" data-testid="pt-org" />
              </div>
              <div>
                <label className="label-field">Partner type *</label>
                <select required value={form.partner_type} onChange={upd("partner_type")} className="input-field" data-testid="pt-type">
                  <option value="">Select type</option>
                  {TYPES.map((x) => <option key={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <label className="label-field">Your name *</label>
                <input required value={form.contact_name} onChange={upd("contact_name")} className="input-field" data-testid="pt-contact" />
              </div>
              <div>
                <label className="label-field">Phone *</label>
                <input required type="tel" value={form.phone} onChange={upd("phone")} className="input-field" data-testid="pt-phone" />
              </div>
              <div className="md:col-span-2">
                <label className="label-field">Work email *</label>
                <input required type="email" value={form.email} onChange={upd("email")} className="input-field" data-testid="pt-email" />
              </div>
              <div className="md:col-span-2">
                <label className="label-field">Notes</label>
                <textarea rows={4} value={form.notes} onChange={upd("notes")} className="input-field" data-testid="pt-notes" placeholder="Typical client volume, discharge needs, coverage area…" />
              </div>
            </div>
            <FormConsent type="referral" checked={termsOk} onChange={(e) => setTermsOk(e.target.checked)} testId="pt-consent" />
            <button disabled={loading} className="btn-primary" data-testid="pt-submit">
              <Handshake className="w-4 h-4" /> {loading ? "Sending…" : "Request partnership"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
